import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, MoreVertical, Pencil, Trash2, Package, Layers, Settings } from "lucide-react";
import { useCombos, Combo } from "@/hooks/useCombos";
import { ComboBasicFormDialog } from "@/components/combos/ComboBasicFormDialog";
import { ComboSlotsSheet } from "@/components/combos/ComboSlotsSheet";
import { useComboSlots } from "@/hooks/useComboSlots";

function ComboSlotCount({ comboId }: { comboId: string }) {
  const { slots, isLoading } = useComboSlots(comboId);
  
  if (isLoading) return <Skeleton className="h-4 w-16" />;
  
  return (
    <span className="text-xs text-muted-foreground">
      {slots.length} {slots.length === 1 ? 'slot' : 'slots'}
    </span>
  );
}

export default function CombosPage() {
  const { combos, isLoading, deleteCombo, toggleActive } = useCombos();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState<Combo | null>(null);
  const [comboToDelete, setComboToDelete] = useState<Combo | null>(null);
  const [slotsSheetCombo, setSlotsSheetCombo] = useState<Combo | null>(null);

  const filteredCombos = combos.filter(combo =>
    combo.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (combo: Combo) => {
    setSelectedCombo(combo);
    setIsFormOpen(true);
  };

  const handleManageSlots = (combo: Combo) => {
    setSlotsSheetCombo(combo);
  };

  const handleDelete = async () => {
    if (comboToDelete) {
      await deleteCombo.mutateAsync(comboToDelete.id);
      setComboToDelete(null);
    }
  };

  const handleToggleActive = async (combo: Combo) => {
    await toggleActive.mutateAsync({ id: combo.id, isActive: !combo.is_active });
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedCombo(null);
  };

  const handleFormSuccess = (comboId: string) => {
    // After creating a new combo, open the slots sheet
    if (!selectedCombo) {
      const newCombo = combos.find(c => c.id === comboId);
      if (newCombo) {
        setSlotsSheetCombo(newCombo);
      } else {
        // Combo might not be in the list yet, create a minimal object
        setSlotsSheetCombo({ id: comboId } as Combo);
      }
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Combos</h1>
          <p className="text-muted-foreground">
            Gerencie combos promocionais do seu cardápio
          </p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Combo
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar combos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Combos Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCombos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "Nenhum combo encontrado" : "Nenhum combo cadastrado"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Tente buscar por outro termo"
                : "Crie combos promocionais para aumentar suas vendas"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsFormOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Combo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCombos.map((combo) => (
            <Card key={combo.id} className={!combo.is_active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      <Package className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{combo.name}</CardTitle>
                      <ComboSlotCount comboId={combo.id} />
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleManageSlots(combo)}>
                        <Layers className="h-4 w-4 mr-2" />
                        Gerenciar Slots
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(combo)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Configurações
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setComboToDelete(combo)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {combo.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {combo.description}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-primary">
                    {formatPrice(combo.price)}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Ativo</span>
                    <Switch
                      checked={combo.is_active}
                      onCheckedChange={() => handleToggleActive(combo)}
                    />
                  </div>
                </div>

                {combo.category?.name && (
                  <p className="text-xs text-muted-foreground">
                    Categoria: {combo.category.name}
                  </p>
                )}

                <div className="flex gap-1 flex-wrap">
                  {combo.available_pdv && (
                    <Badge variant="secondary" className="text-xs">PDV</Badge>
                  )}
                  {combo.available_table && (
                    <Badge variant="secondary" className="text-xs">QR Code</Badge>
                  )}
                  {combo.available_tablet && (
                    <Badge variant="secondary" className="text-xs">Tablet</Badge>
                  )}
                  {combo.available_delivery && (
                    <Badge variant="secondary" className="text-xs">Delivery</Badge>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => handleManageSlots(combo)}
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Configurar Itens
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Basic Form Dialog (Step 1) */}
      <ComboBasicFormDialog
        open={isFormOpen}
        onOpenChange={handleCloseForm}
        combo={selectedCombo}
        onSuccess={handleFormSuccess}
      />

      {/* Slots Sheet (Step 2) */}
      <ComboSlotsSheet
        open={!!slotsSheetCombo}
        onOpenChange={(open) => !open && setSlotsSheetCombo(null)}
        combo={slotsSheetCombo}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!comboToDelete} onOpenChange={() => setComboToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir combo?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o combo "{comboToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
